// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { BaseModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export type ReorderPadProductListViewType = "Grid" | "Table";

export interface ReorderPadProductFilters extends HasPagingParameters {
    searchWithin?: string[];
    fromDate?: string;
    toDate?: string;
}

export interface ReorderPadModal extends BaseModel {
    lastOrderedQty?: string;
    lastOrderedDate?: string;
    productERPNumber?: string;
}

export interface ReorderPadApiModal {
    items?: ReorderPadModal[];
    pagination: PaginationModel | null;
}

export interface ReorderPadState {
    isLoading: boolean;
    views?: ReorderPadApiModal;
    failedMessage?: string;
    productFilters: ReorderPadProductFilters;
    filterQuery?: string;
    view?: ReorderPadProductListViewType;
}

const initialState: ReorderPadState = {
    isLoading: false,
    views: undefined,
    failedMessage: "",
    view: "Table",
    productFilters: {},
};

const reducer = {
    "xReorderPad/BeginLoadReorderPad": (draft: Draft<ReorderPadState>, action: {}) => {
        draft.isLoading = true;
        draft.failedMessage = "";
    },
    "xReorderPad/FailedToLoadReorderPad": (draft: Draft<ReorderPadState>, action: { failedMessage: string }) => {
        draft.isLoading = false;
        draft.failedMessage = action.failedMessage;
    },
    "xReorderPad/CompleteLoadReorderPad": (draft: Draft<ReorderPadState>, action: { data: ReorderPadApiModal }) => {
        draft.isLoading = false;
        draft.views = action.data;
        draft.failedMessage = "";
    },
    "xReorderPad/SetProductFilters": (draft: Draft<ReorderPadState>, action: { result: ReorderPadProductFilters }) => {
        draft.productFilters = action.result;
    },
    "xReorderPad/SetFilterQuery": (draft: Draft<ReorderPadState>, action: { result: string }) => {
        draft.filterQuery = action.result;
    },
    "xReorderPad/SetView": (
        draft: Draft<ReorderPadState>,
        action: { parameter: { view: ReorderPadProductListViewType } },
    ) => {
        draft.view = action.parameter.view;
    },
};

const xReorderPad = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xReorderPad = xReorderPad;

export type xReorderPadState = {
    xReorderPad: ReorderPadState;
} & ApplicationState;
